import { toApiError, type PaginatedResponse, type PaginationMeta } from "@/shared/api/common";
import { http } from "@/shared/api/http";
import type { AppLanguage } from "@/shared/config/i18n/config";

export type Country = {
  id: string;
  nameUz: string | null;
  nameRu: string | null;
  nameEn: string | null;
  names: string | null;
  parentId: string | null;
};

export type CountriesQueryParams = {
  page?: number;
  limit?: number | "all";
  search?: string;
  parentId?: string | null;
};

type CountryApi = {
  id: string;
  name_uz: string | null;
  name_ru: string | null;
  name_en: string | null;
  names: string | null;
  parent_id: string | null;
};

function toCountry(row: CountryApi): Country {
  return {
    id: row.id,
    nameUz: row.name_uz,
    nameRu: row.name_ru,
    nameEn: row.name_en,
    names: row.names,
    parentId: row.parent_id,
  };
}

export async function fetchCountries(
  params: CountriesQueryParams = {},
): Promise<{ data: Country[]; pagination: PaginationMeta }> {
  const normalizedSearch = typeof params.search === "string" ? params.search.trim() : "";

  try {
    const response = await http.get<PaginatedResponse<CountryApi>>("/analytics/countries", {
      params: {
        page: params.page,
        limit: params.limit,
        q: normalizedSearch || undefined,
        parent_id: params.parentId ?? undefined,
      },
    });

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    const page = params.page ?? 1;
    const limit = params.limit ?? 100;
    const numericLimit = typeof limit === "number" ? limit : Math.max(rows.length, 1);

    return {
      data: rows.map(toCountry),
      pagination: response.data?.pagination ?? {
        page,
        limit: numericLimit,
        total: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / numericLimit)),
      },
    };
  } catch (error) {
    throw toApiError(error, "Unable to load countries.");
  }
}

/**
 * Localized country name:
 *   ru, uz-cyrl → name_ru
 *   en          → name_en
 *   uz (default)→ name_uz
 * Falls back through the other names before returning the id as a last resort.
 */
export function getCountryDisplayName(country: Country, language: string): string {
  const lang = language as AppLanguage;
  const candidates: (string | null)[] =
    lang === "ru" || lang === "uz-cyrl"
      ? [country.nameRu, country.nameUz, country.nameEn]
      : lang === "en"
        ? [country.nameEn, country.nameUz, country.nameRu]
        : [country.nameUz, country.nameRu, country.nameEn];

  return candidates.find((value): value is string => typeof value === "string" && value.length > 0) ?? country.id;
}

/**
 * Same locale selection as `getCountryDisplayName`, but accepts the snake_case
 * shape that comes straight from the API (e.g. `vendor.country` in the
 * wholesale-offers response). Returns null when the object is missing or empty.
 */
export function getApiCountryDisplayName(
  country:
    | {
        id?: string | null;
        name_uz?: string | null;
        name_ru?: string | null;
        name_en?: string | null;
      }
    | null
    | undefined,
  language: string,
): string | null {
  if (!country) return null;
  const lang = language as AppLanguage;
  const candidates: (string | null | undefined)[] =
    lang === "ru" || lang === "uz-cyrl"
      ? [country.name_ru, country.name_uz, country.name_en]
      : lang === "en"
        ? [country.name_en, country.name_uz, country.name_ru]
        : [country.name_uz, country.name_ru, country.name_en];

  return candidates.find((value): value is string => typeof value === "string" && value.length > 0) ?? null;
}
